import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Plus, X, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { MOCK_PACKAGES } from '../data/mockData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [date, setDate] = useState(null);
  const [newBooking, setNewBooking] = useState({
    package_id: '',
    address: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, packagesRes] = await Promise.all([
        fetch(`${API}/bookings`, { credentials: 'include' }).catch(err => {
          console.warn('Failed to fetch bookings:', err);
          return { ok: false };
        }),
        fetch(`${API}/packages`).catch(err => {
          console.warn('Failed to fetch packages:', err);
          return { ok: false };
        })
      ]);

      if (bookingsRes && bookingsRes.ok) {
        const contentType = bookingsRes.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await bookingsRes.json();
          setBookings(data);
        }
      }

      let packagesLoaded = false;
      if (packagesRes && packagesRes.ok) {
        const contentType = packagesRes.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await packagesRes.json();
          if (Array.isArray(data) && data.length > 0) {
            setPackages(data);
            packagesLoaded = true;
          }
        }
      }
      
      if (!packagesLoaded) {
        console.warn('Using mock packages fallback in Bookings.');
        setPackages(MOCK_PACKAGES);
      }
    } catch (error) {
      console.error('Failed to fetch data, using mock packages fallback:', error);
      setPackages(MOCK_PACKAGES);
    } finally {
      setLoading(false);
    }
  };

  const createBooking = async (e) => {
    e.preventDefault();
    if (!date || !newBooking.package_id || !newBooking.address) {
      toast.error('Please fill all required fields');
      return;
    }

    setCreating(true);
    try {
      const res = await fetch(`${API}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newBooking,
          scheduled_date: date.toISOString()
        })
      });

      if (res.ok) {
        const created = await res.json();
        setBookings([created, ...bookings]);
        setDialogOpen(false);
        setNewBooking({ package_id: '', address: '', notes: '' });
        setDate(null);
        toast.success('Booking created successfully!');
      } else {
        toast.error('Failed to create booking');
      }
    } catch (error) {
      console.error('Create booking error:', error);
      toast.error('Something went wrong');
    } finally {
      setCreating(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    try {
      const res = await fetch(`${API}/bookings/${bookingId}/cancel`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (res.ok) {
        setBookings(bookings.map(b => 
          b.booking_id === bookingId ? { ...b, status: 'cancelled' } : b
        ));
        toast.success('Booking cancelled');
      } else {
        toast.error('Failed to cancel booking');
      }
    } catch (error) {
      console.error('Cancel booking error:', error);
      toast.error('Something went wrong');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
      confirmed: 'text-green-400 bg-green-400/10 border-green-400/20',
      completed: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
      cancelled: 'text-red-400 bg-red-400/10 border-red-400/20'
    };
    return colors[status] || 'text-gray-400 bg-gray-400/10';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getPackageName = (packageId) => {
    const pkg = packages.find(p => p.package_id === packageId);
    return pkg?.name || packageId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="bookings-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
      >
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Service Bookings</h1>
          <p className="text-gray-400">Schedule and manage your service appointments</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:opacity-95 text-white font-semibold shadow-[0_4px_12px_rgba(59,130,246,0.2)] border-none"
              data-testid="schedule-booking-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Schedule Service
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0D1224] border-blue-500/15 text-white max-w-lg shadow-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white">Schedule a Service</DialogTitle>
            </DialogHeader>
            <form onSubmit={createBooking} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2 font-medium">Service Package *</label>
                <Select 
                  value={newBooking.package_id}
                  onValueChange={(value) => setNewBooking({...newBooking, package_id: value})}
                >
                  <SelectTrigger className="bg-[#080B14]/40 border-blue-500/15 text-white focus:border-blue-500/50" data-testid="booking-package-select">
                    <SelectValue placeholder="Select a package" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0D1224] border-blue-500/15 text-white">
                    {packages.map(pkg => (
                      <SelectItem key={pkg.package_id} value={pkg.package_id}>
                        {pkg.name} - ₹{pkg.price}/mo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2 font-medium">Preferred Date *</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-[#080B14]/40 border-blue-500/15 text-white hover:bg-blue-500/5 focus:border-blue-500/50"
                      data-testid="booking-date-picker"
                    >
                      <Calendar className="mr-2 h-4 w-4 text-gray-400" />
                      {date ? format(date, 'PPP') : <span className="text-gray-500">Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#0D1224] border-blue-500/15" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                      className="rounded-md"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2 font-medium">Service Address *</label>
                <Input
                  value={newBooking.address}
                  onChange={(e) => setNewBooking({...newBooking, address: e.target.value})}
                  required
                  placeholder="Enter your service location"
                  className="bg-[#080B14]/40 border-blue-500/15 text-white placeholder:text-gray-500 focus:border-blue-500/50"
                  data-testid="booking-address-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2 font-medium">Additional Notes</label>
                <Textarea
                  value={newBooking.notes}
                  onChange={(e) => setNewBooking({...newBooking, notes: e.target.value})}
                  rows={3}
                  placeholder="Any special requirements or instructions..."
                  className="bg-[#080B14]/40 border-blue-500/15 text-white resize-none placeholder:text-gray-500 focus:border-blue-500/50"
                  data-testid="booking-notes-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 border-blue-500/20 text-white bg-transparent hover:bg-blue-500/5"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-400 hover:opacity-95 text-white font-semibold border-none shadow-[0_2px_10px_rgba(59,130,246,0.2)]"
                  data-testid="submit-booking-btn"
                >
                  {creating ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Schedule'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </motion.div>

      {/* Bookings List */}
      {bookings.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No bookings yet</h3>
          <p className="text-gray-400 mb-6">Schedule your first service appointment to get started.</p>
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white font-semibold border-none hover:shadow-[0_0_30px_rgba(59,130,246,0.3)] shadow-[0_4px_12px_rgba(59,130,246,0.2)]"
          >
            Schedule Your First Service
          </Button>
        </motion.div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {bookings.map((booking, idx) => (
            <motion.div
              key={booking.booking_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="p-6 rounded-xl border group"
              style={{ background: '#0D1224', border: '1px solid rgba(99, 130, 255, 0.12)' }}
              data-testid={`booking-${booking.booking_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-white font-semibold mb-1 group-hover:text-blue-400 transition-colors">
                    {getPackageName(booking.package_id)}
                  </h3>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking.status)}`}>
                    {booking.status}
                  </span>
                </div>
                {(booking.status === 'pending' || booking.status === 'confirmed') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => cancelBooking(booking.booking_id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                    data-testid={`cancel-booking-${booking.booking_id}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3 text-gray-400">
                  <Calendar className="w-4 h-4 text-blue-400" />
                  <span>{formatDate(booking.scheduled_date)}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <MapPin className="w-4 h-4 text-blue-400" />
                  <span className="line-clamp-1">{booking.address}</span>
                </div>
                {booking.notes && (
                  <div className="flex items-start gap-3 text-gray-400">
                    <AlertCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="line-clamp-2">{booking.notes}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-blue-500/10 flex items-center justify-between">
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3 text-blue-400" />
                  Booked on {new Date(booking.created_at).toLocaleDateString()}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
